import React, { useState, useEffect, useMemo } from 'react';
import Header from '../Components/Header';
import AuctionChart from '../Components/AuctionChart';
import { formatPrice } from '../services/auctionService';
import { db } from '../firebase';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import './CarPage.css';
import Footer from '../Components/Footer';

// Timeframe options are calendar months. Add more entries here as new
// months become selectable in the dropdown.
const TIMEFRAME_OPTIONS = [
    { label: 'October', start: new Date(2026, 9, 1), end: new Date(2026, 10, 1) },
    { label: 'September', start: new Date(2026, 8, 1), end: new Date(2026, 9, 1) }
];

const getTimeframeRange = (label) =>
    TIMEFRAME_OPTIONS.find(o => o.label === label) || TIMEFRAME_OPTIONS[0];

// The previous period is the calendar month immediately before the
// selected month (e.g. August 2026 for September 2026).
const getPreviousRange = (range) => ({
    start: new Date(range.start.getFullYear(), range.start.getMonth() - 1, 1),
    end: range.start
});

// Model filter column layout. marketModel values matching each entry render
// in that column in this order; anything not listed falls into a final
// catch-all column (sorted alphabetically).
const MODEL_FILTER_COLUMNS = [
    ['356'],
    ['911 (Classic)', '911 (G-Body)', '911 (964)', '911 (993)', '911 (996)', '911 (997)', '911 (991)', '911 (992)'],
    ['911 Turbo (930)', '911 Turbo (964)', '911 Turbo (993)', '911 Turbo (996)', '911 Turbo (997)', '911 Turbo (991)', '911 Turbo (992)'],
    ['912', '914', '924', '928', '944', '968'],
    ['Boxster (986)', 'Boxster/Cayman (987)', 'Boxster/Cayman (981)', 'Boxster/Cayman (718)']
];
const MODEL_FILTER_LISTED = new Set(MODEL_FILTER_COLUMNS.flat());

const MarketDashboard = () => {
    const [auctionStats, setAuctionStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [auctionData, setAuctionData] = useState([]);
    const [modelMap, setModelMap] = useState({});
    const [timeframe, setTimeframe] = useState(TIMEFRAME_OPTIONS[0].label);
    const [sortOption, setSortOption] = useState("Date: New to Old");
    const [filters, setFilters] = useState({ marketModel: new Set() });
    const [uniqueValues, setUniqueValues] = useState({ marketModel: [] });
    const [hoveredAuction, setHoveredAuction] = useState(null);
    const [hoveredFilter, setHoveredFilter] = useState({ category: null, value: null });
    const [previousPeriodStats, setPreviousPeriodStats] = useState(null);

    const getAuctionDate = (auction) =>
        auction.auctionDate?.toDate?.() || new Date(auction.auctionDate);

    const filterDataByRange = (data, range) =>
        data.filter(auction => {
            const auctionDate = getAuctionDate(auction);
            return auctionDate >= range.start && auctionDate < range.end;
        });

    // Function to calculate stats from filtered data
    const calculateStats = (data) => {
        let totalPrice = 0;
        let highestPrice = 0;
        let lowestPrice = Infinity;
        let soldCount = 0;

        data.forEach(auction => {
            const finalBid = parseFloat(auction.finalBid);
            if (auction.sold) {
                soldCount++;
                totalPrice += finalBid;
                highestPrice = Math.max(highestPrice, finalBid);
                lowestPrice = Math.min(lowestPrice, finalBid);
            }
        });

        return {
            sellThroughRate: data.length > 0 ? Number(((soldCount / data.length) * 100).toFixed(1)) : 0,
            dollarVolume: totalPrice,
            averagePrice: soldCount > 0 ? totalPrice / soldCount : 0,
            highestPrice: highestPrice,
            lowestPrice: lowestPrice === Infinity ? 0 : lowestPrice
        };
    };

    // Function to apply the marketModel filter to data
    const applyFilters = (data) => {
        return data.filter(item => {
            if (!filters.marketModel || filters.marketModel.size === 0) return true;
            const itemValue = item.marketModel?.toString();
            if (!itemValue) return true;
            return filters.marketModel.has(itemValue);
        });
    };

    // Function to handle filter changes
    const handleFilterChange = (category, value, checked) => {
        setFilters(prev => {
            const newSet = new Set(prev[category]);
            if (checked) {
                newSet.add(value);
            } else {
                newSet.delete(value);
            }
            return { ...prev, [category]: newSet };
        });
    };

    // Select All: check every value when not all are selected, clear all
    // when they are
    const handleSelectAll = (checked) => {
        setFilters(prev => ({
            ...prev,
            marketModel: checked ? new Set(uniqueValues.marketModel) : new Set()
        }));
    };

    // Function to format mileage with commas
    const formatMileage = (miles) => {
        if (!miles) return '0';
        const numericValue = miles.toString().replace(/[^0-9]/g, '');
        return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    // Function to check if a filter value matches the hovered auction
    const isHighlighted = (category, value) => {
        if (!hoveredAuction) return false;
        return hoveredAuction[category]?.toString() === value;
    };

    // Fetch model lines once (for marketModel join and tile fallbacks)
    useEffect(() => {
        const fetchModelLines = async () => {
            try {
                const modelSnapshot = await getDocs(collection(db, 'modelLines'));
                const map = {};
                modelSnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.modelId) map[data.modelId] = data;
                });
                setModelMap(map);
            } catch (error) {
                console.error('Error fetching model lines:', error);
            }
        };
        fetchModelLines();
    }, []);

    // Real-time listener for all auction data
    useEffect(() => {
        const unsubscribe = onSnapshot(collection(db, 'auctions'), (querySnapshot) => {
            const auctionDataArray = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAuctionData(auctionDataArray);
            setLoading(false);
        }, (error) => {
            console.error('Error fetching auction data:', error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    // Join each auction to its model line so marketModel filtering and
    // chart highlighting work like any other auction field
    const auctionsWithMarket = useMemo(() => {
        return auctionData.map(auction => ({
            ...auction,
            marketModel: modelMap[auction.modelId]?.marketModel || '',
            marketModelData: modelMap[auction.modelId] || null
        }));
    }, [auctionData, modelMap]);

    const completedAuctions = useMemo(() => {
        return auctionsWithMarket.filter(a => a.finalBid != null && a.finalBid !== '');
    }, [auctionsWithMarket]);

    const timeframeRange = useMemo(() => getTimeframeRange(timeframe), [timeframe]);

    // Chart x-axis range: pad one day before the month starts and cap at the
    // end of the month's last day so the first tick isn't flush on the edge
    // and the following month (e.g. Oct 1) never shows up as a tick
    const chartRange = useMemo(() => ({
        start: new Date(timeframeRange.start.getFullYear(), timeframeRange.start.getMonth(), timeframeRange.start.getDate() - 1),
        end: new Date(timeframeRange.end.getTime() - 1)
    }), [timeframeRange]);

    const timeframeData = useMemo(() => {
        return filterDataByRange(completedAuctions, timeframeRange);
    }, [completedAuctions, timeframeRange]);

    const filteredData = useMemo(() => {
        return applyFilters(timeframeData);
    }, [timeframeData, filters]);

    // Unique marketModel values within the selected timeframe
    useEffect(() => {
        const values = new Set();
        timeframeData.forEach(item => {
            const value = item.marketModel?.toString();
            if (value) values.add(value);
        });
        const sorted = values.size > 1 ? Array.from(values).sort() : [];
        setUniqueValues({ marketModel: sorted });
        setFilters({ marketModel: new Set(sorted) });
    }, [timeframeData]);

    // Split available marketModel values into the fixed display columns.
    // Values absent from the current timeframe are skipped; unlisted values
    // fall into a final catch-all column.
    const modelFilterColumns = useMemo(() => {
        const values = uniqueValues.marketModel || [];
        const available = new Set(values);
        const columns = MODEL_FILTER_COLUMNS.map(column => column.filter(v => available.has(v)));
        columns.push(values.filter(v => !MODEL_FILTER_LISTED.has(v)).sort());
        return columns.filter(column => column.length > 0);
    }, [uniqueValues]);

    // Current and previous period stats
    useEffect(() => {
        const stats = calculateStats(applyFilters(timeframeData));
        setAuctionStats(stats);

        const prevData = filterDataByRange(completedAuctions, getPreviousRange(timeframeRange));
        const filteredPrevData = applyFilters(prevData);
        const prevStats = calculateStats(filteredPrevData);
        prevStats.unitVolume = filteredPrevData.length;
        setPreviousPeriodStats(prevStats);
    }, [filters, timeframeData, completedAuctions, timeframeRange]);

    const sortedAuctionData = useMemo(() => {
        if (!filteredData) return [];

        const dataToSort = [...filteredData];

        switch (sortOption) {
            case "Date: New to Old":
                return dataToSort.sort((a, b) => getAuctionDate(b) - getAuctionDate(a));
            case "Date: Old to New":
                return dataToSort.sort((a, b) => getAuctionDate(a) - getAuctionDate(b));
            case "Price: High to Low":
                return dataToSort.sort((a, b) => parseFloat(b.finalBid) - parseFloat(a.finalBid));
            case "Price: Low to High":
                return dataToSort.sort((a, b) => parseFloat(a.finalBid) - parseFloat(b.finalBid));
            case "Miles: High to Low":
                return dataToSort.sort((a, b) => {
                    const milesA = parseFloat(a.miles?.replace(/[^0-9]/g, '')) || 0;
                    const milesB = parseFloat(b.miles?.replace(/[^0-9]/g, '')) || 0;
                    return milesB - milesA;
                });
            case "Miles: Low to High":
                return dataToSort.sort((a, b) => {
                    const milesA = parseFloat(a.miles?.replace(/[^0-9]/g, '')) || 0;
                    const milesB = parseFloat(b.miles?.replace(/[^0-9]/g, '')) || 0;
                    return milesA - milesB;
                });
            default:
                return dataToSort;
        }
    }, [filteredData, sortOption]);

    const handleTimeframeChange = (e) => {
        setTimeframe(e.target.value);
    };

    // If still loading, show loading state
    if (loading) {
        return (
            <div className="betasml-carpage">
                <Header />
                <main className="carpage-content">
                    <div>Loading auction data...</div>
                </main>
            </div>
        );
    }

    // Fallback stats so the page still renders when there is no auction data yet
    const safeStats = auctionStats || {
        sellThroughRate: 0,
        dollarVolume: 0,
        averagePrice: 0,
        lowestPrice: 0,
        highestPrice: 0
    };

    const hasPreviousPeriodData = (previousPeriodStats?.unitVolume || 0) > 0;
    const volDelta = filteredData.length - (previousPeriodStats?.unitVolume || 0);
    const strDelta = (auctionStats?.sellThroughRate || 0) - (previousPeriodStats?.sellThroughRate || 0);
    const avgDelta = (auctionStats?.averagePrice || 0) - (previousPeriodStats?.averagePrice || 0);
    const lowDelta = (auctionStats?.lowestPrice || 0) - (previousPeriodStats?.lowestPrice || 0);
    const highDelta = (auctionStats?.highestPrice || 0) - (previousPeriodStats?.highestPrice || 0);

    const deltaColor = (val) => val > 0 ? '#2ecda7' : val < 0 ? '#e85d75' : '#888';
    const deltaSign = (val) => val > 0 ? '+' : '';
    const renderDelta = (content, value) => {
        if (!hasPreviousPeriodData) {
            return <p className="trend-delta" style={{ color: '#888' }}>--</p>;
        }
        return (
            <p className="trend-delta" style={{ color: deltaColor(value) }}>
                {content}
            </p>
        );
    };

    return (
        <div className="betasml-carpage">
            <Header />
            <main className="carpage-content">
                {/* Left Panel */}
                <section className="left-panel">
                    <div className="trend-analysis-tile">
                        <h3>Trend Analysis</h3>
                        <p className="trend-subtitle">
                            {hasPreviousPeriodData ? 'Compared to Previous Period' : 'No previous-period data yet'}
                        </p>
                        <div className="trend-stat-row">
                            <p className="stat-title">Unit Volume</p>
                            {renderDelta(`${deltaSign(volDelta)}${volDelta}`, volDelta)}
                        </div>
                        <div className="trend-stat-row">
                            <p className="stat-title">Sell-Through Rate</p>
                            {renderDelta(`${deltaSign(strDelta)}${Number(strDelta.toFixed(1))}%`, strDelta)}
                        </div>
                        <div className="trend-stat-row">
                            <p className="stat-title">Average Price</p>
                            {renderDelta(`${avgDelta < 0 ? '-' : avgDelta > 0 ? '+' : ''}${formatPrice(Math.abs(avgDelta))}`, avgDelta)}
                        </div>
                        <div className="trend-stat-row">
                            <p className="stat-title">Low</p>
                            {renderDelta(`${lowDelta < 0 ? '-' : lowDelta > 0 ? '+' : ''}${formatPrice(Math.abs(lowDelta))}`, lowDelta)}
                        </div>
                        <div className="trend-stat-row">
                            <p className="stat-title">High</p>
                            {renderDelta(`${highDelta < 0 ? '-' : highDelta > 0 ? '+' : ''}${formatPrice(Math.abs(highDelta))}`, highDelta)}
                        </div>
                    </div>
                </section>

                {/* Right Section */}
                <section className="right-section">
                    <div className="dropdown-container">
                        <select
                            className="timeframe-select"
                            value={timeframe}
                            onChange={handleTimeframeChange}
                        >
                            {TIMEFRAME_OPTIONS.map(option => (
                                <option key={option.label}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="placeholder-chart-container">
                        {loading ? (
                            <p>Loading auction data...</p>
                        ) : (
                            <>
                                <div className="chart-legend">
                                    <div className="legend-item">
                                        <div className="legend-dot sold"></div>
                                        <span>Sold</span>
                                    </div>
                                    <div className="legend-item">
                                        <div className="legend-dot not-sold"></div>
                                        <span>Not Sold</span>
                                    </div>
                                </div>
                                <AuctionChart
                                    auctionData={filteredData}
                                    timeframe={timeframe}
                                    allData={filteredData}
                                    onDataPointHover={setHoveredAuction}
                                    sortBy="date"
                                    hoveredFilter={hoveredFilter}
                                    xMin={chartRange.start}
                                    xMax={chartRange.end}
                                />
                                <div className="stats-row">
                                    <div className="stat-item">
                                        <p className="stat-title">Unit Volume</p>
                                        <p className="stat-value">{filteredData.length}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Sell-Through Rate</p>
                                        <p className="stat-value">{`${safeStats.sellThroughRate}%`}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Dollar Volume</p>
                                        <p className="stat-value">{formatPrice(safeStats.dollarVolume)}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Average Price</p>
                                        <p className="stat-value">{formatPrice(safeStats.averagePrice)}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Low</p>
                                        <p className="stat-value">{formatPrice(safeStats.lowestPrice)}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">High</p>
                                        <p className="stat-value">{formatPrice(safeStats.highestPrice)}</p>
                                    </div>
                                </div>
                                {modelFilterColumns.length > 0 && (
                                <>
                                <hr className="filter-separator" />
                                <div className="filter-section">
                                    <div className="filter-category">
                                        <h3>Model</h3>
                                        <div className="filter-item" style={{ marginBottom: '10px' }}>
                                            <input
                                                type="checkbox"
                                                checked={uniqueValues.marketModel.length > 0 && uniqueValues.marketModel.every(v => filters.marketModel.has(v))}
                                                ref={el => {
                                                    if (el) {
                                                        const selected = uniqueValues.marketModel.filter(v => filters.marketModel.has(v)).length;
                                                        el.indeterminate = selected > 0 && selected < uniqueValues.marketModel.length;
                                                    }
                                                }}
                                                onChange={(e) => handleSelectAll(e.target.checked)}
                                            />
                                            <label>Select All</label>
                                        </div>
                                        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                                            {modelFilterColumns.map((column, colIndex) => (
                                                <div key={colIndex}>
                                                    {column.map(value => (
                                                        <div key={value} className="filter-item">
                                                            <input
                                                                type="checkbox"
                                                                checked={filters.marketModel.has(value)}
                                                                onChange={(e) => handleFilterChange('marketModel', value, e.target.checked)}
                                                                className={isHighlighted('marketModel', value) ? 'highlighted' : ''}
                                                                onMouseEnter={() => setHoveredFilter({ category: 'marketModel', value })}
                                                                onMouseLeave={() => setHoveredFilter({ category: null, value: null })}
                                                            />
                                                            <label
                                                                className={isHighlighted('marketModel', value) ? 'highlighted' : ''}
                                                                onMouseEnter={() => setHoveredFilter({ category: 'marketModel', value })}
                                                                onMouseLeave={() => setHoveredFilter({ category: null, value: null })}
                                                            >
                                                                {value}
                                                            </label>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                </>
                                )}
                            </>
                        )}
                    </div>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '95%',
                        marginTop: '2rem',
                        marginBottom: '1rem'
                    }}>
                        <h2 className="latest-auction-results" style={{ margin: 0, whiteSpace: 'nowrap' }}>Auction Results</h2>
                        <select
                            value={sortOption}
                            onChange={(e) => setSortOption(e.target.value)}
                            style={{
                                padding: '8px',
                                borderRadius: '4px',
                                border: '1px solid #ccc',
                                backgroundColor: 'white'
                            }}
                        >
                            <option value="Date: New to Old">Date: New to Old</option>
                            <option value="Date: Old to New">Date: Old to New</option>
                            <option value="Price: High to Low">Price: High to Low</option>
                            <option value="Price: Low to High">Price: Low to High</option>
                            <option value="Miles: High to Low">Miles: High to Low</option>
                            <option value="Miles: Low to High">Miles: Low to High</option>
                        </select>
                    </div>
                    <div className="auction-grid-container" style={{ width: '95%' }}>
                        {!loading && sortedAuctionData.map((auction, index) => {
                            const Wrapper = auction.auctionLink ? 'a' : 'div';
                            const fallbackModel = auction.marketModelData;
                            return (
                                <Wrapper
                                    key={index}
                                    {...(auction.auctionLink ? {
                                        href: auction.auctionLink,
                                        target: "_blank",
                                        rel: "noopener noreferrer"
                                    } : {})}
                                    className="auction-result-tile"
                                >
                                    <img
                                        src={auction.imageLink || fallbackModel?.imageLink}
                                        alt={auction.title || (fallbackModel ? `${fallbackModel.modelLine} ${fallbackModel.model}` : 'Auction')}
                                        className={`auction-result-image ${!auction.imageLink ? 'fallback-image' : ''}`}
                                    />
                                    <div className="auction-result-header">
                                        <h3 className="auction-result-title">
                                            {auction.title || (fallbackModel ? `${fallbackModel.modelLine} ${fallbackModel.model}` : 'Auction')}
                                        </h3>
                                        <div className="auction-result-mileage">
                                            {auction.miles ? `${formatMileage(auction.miles)} miles` : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="auction-result-status-row">
                                        <div className="auction-result-status">
                                            <span className={`status-dot ${auction.sold ? 'sold' : 'unsold'}`}></span>
                                            <span>{auction.sold ? 'Sold' : 'Unsold'}</span>
                                        </div>
                                        <span className="auction-result-price">${auction.finalBid.toLocaleString()}</span>
                                    </div>
                                    <div className="auction-details-group">
                                        <div className="auction-result-colors">
                                            <div className="color-row">
                                                <span className="color-label">Exterior Color:</span>
                                                <span className="color-value">{auction.exteriorColor}</span>
                                            </div>
                                            <div className="color-row">
                                                <span className="color-label">Interior Color:</span>
                                                <span className="color-value">{auction.interiorColor}</span>
                                            </div>
                                        </div>
                                        <p className="auction-result-date">
                                            {auction.auctionDate?.seconds ?
                                                new Date(auction.auctionDate.seconds * 1000).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })
                                                : 'Date not available'
                                            }
                                        </p>
                                    </div>
                                    <div className="auction-result-house">
                                        <span>{auction.auctionHouse}</span>
                                        {auction.auctionLink && (
                                            <svg className="external-link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                </Wrapper>
                            );
                        })}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default MarketDashboard;
